/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import config from '../../../../share/config';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTE: 'execute',
  EXECUTING: 'executing',
  SUCCESS: 'success',
  FAILED_EXECUTE: 'failedExecute',
  FAILED_ENVIRONMENT: 'failedEnvironment',
  FAILED: 'failed'
};

export default {
  name: 'requirement086',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isDisabled: false,
      displayInfoFailedExecute: '', // 調整失敗エラー画面メッセージ
      modal_requirement_086: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.START:
        case PHASE.EXECUTE:
        case PHASE.EXECUTING:
          return 'secondary';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.FAILED_EXECUTE:
        case PHASE.FAILED_ENVIRONMENT:
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    // 初期化メソッドを呼び出す
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化メソッド */
    async init() {
      this.modal_requirement_086 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 終了処理エラー判定フラグ(初期状態はエラー判定無し)
      this.isErrorCheck = false;
    },

    //----- htmlイベントメソッド -----------------------------------------------//

    /** 画面を閉じる（終了処理を実行する）ボタンクリックイベント */
    async clickClose() {
      // 終了判定結果の取得
      if (await this.exitPostGet()) {
        this.$emit('close-wizard');
        this.modal_requirement_086 = false;
      }
    },

    /** 待機画面OKボタンクリックイベント */
    async clickExecute() {
      // 実行画面を表示
      this.showExecute();
    },

    /** 実行画面OKボタンクリックイベント */
    async clickExecuting() {
      // 本遷移処理以降は終了処理でエラー判定を行う
      this.isErrorCheck = true;
      // 実行監視画面を表示
      this.showExecuting();
      // ルーチン実行メソッドを呼び出す
      await this.executePostGet();
    },

    //----- PHASE操作メソッド -----------------------------------------------//

    /** 実行表示メソッド */
    showExecute() {
      this.phase = PHASE.EXECUTE;
    },

    /** 実行監視表示メソッド */
    showExecuting() {
      this.phase = PHASE.EXECUTING;
    },

    /** 正常終了表示メソッド */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** 調整失敗エラー表示メソッド */
    showFailedExecute () {
      this.phase = PHASE.FAILED_EXECUTE;
    },

    /** 調整環境不備エラー表示メソッド */
    showFailedEnvironment () {
      this.phase = PHASE.FAILED_ENVIRONMENT;
    },

    /** 共通エラー表示メソッド */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行メソッド -----------------------------------------------//

    /**
     * ルーチン実行メソッド
     */
    async executePostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // データ読み出しメソッドを呼び出す
            await this.readPostGet(PHASE.SUCCESS);
          } else {
            // [成功以外]
            switch (response.data.async_code) {
              case error.WSP_EXECUTION_RADAR_CONTROL_FAILURE_ERROR:
                // [調整失敗エラー]
                // データ読み出しメソッドを呼び出す
                await this.readPostGet(PHASE.FAILED_EXECUTE);
                break;
              case error.WSP_EXECUTION_RADAR_CONTROL_ENVIRONMENT_FAILURE_ERROR:
                // [調整環境不備エラー]
                // 調整環境不備エラー画面に遷移
                this.showFailedEnvironment();
                break;
              default:
                // [上記以外のエラー]
                // 共通エラー画面に遷移
                this.showUnexpectedError();
                break;
            }
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

   /**
    * データ読み出しメソッド
    * ルーチン実行結果が成功、または、調整失敗エラーの場合に呼び出す
    * @param {string} phase 遷移先フェーズ
    */
    async readPostGet(phase) {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            switch (phase) {
              case PHASE.SUCCESS:
                // 正常終了画面に遷移
                this.showSuccess();
                break;
              case PHASE.FAILED_EXECUTE:
                // 軸ずれ量を変換して画面メッセージを設定
                this.displayInfoFailedExecute = this.$t(`${this.i18nWsKey}.REQ_WS_86_TXT_5_1101`)
                                                 .replace('$DID$', response.data.user_data.deg);
                // 調整失敗エラー画面に遷移
                this.showFailedExecute();
                break;
            }
          } else {
            // [成功以外]
            // 共通エラー画面に遷移
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 終了処理
     * @return 終了判定フラグ
     */
    async exitPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; if (this.isErrorCheck) this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (this.isErrorCheck) {
          // 終了処理のエラー判定を行う場合
          if (response) {
            // レスポンスが取得できた場合
            if (response.data.status === config.STATUS_WSP_COMPLETED) {
              // [成功]
              return true;
            } else {
              // [成功以外]
              // 共通エラー画面に遷移
              this.showUnexpectedError();
              return false;
            }
          } else {
            // レスポンスが取得できない場合、コールバックでエラー画面に遷移
            return false;
          }
        } else {
          return true;
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; if (this.isErrorCheck) this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー画面では終了処理でエラー判定を行わない
      this.isErrorCheck = false;
      // 共通エラー画面に遷移
      this.showFailed();
    },

  }
};